<template>
  <div class="stc-compact-product" data-testid="stc-compact-product" :data-product-id="productId">
    <button
      type="button"
      class="stc-compact-product__overlay-btn"
      :aria-label="`View details for ${productName}`"
      :disabled="multiATBStore.isLoading"
      @click="handleProductClick"
    ></button>

    <div class="stc-compact-product__main">
      <div class="stc-compact-product__image-container" data-testid="stc-compact-product-image">
        <Picture v-if="primaryImage" :image-url="primaryImage" :alt="productImgAlt" :image-sizes-by-breakpoints="imageSizes" :ai-image />
      </div>

      <div class="stc-compact-product__details">
        <div class="title-wrap">
          <div class="stc-compact-product__title" :title="productName" data-testid="stc-compact-product-title">
            {{ productName }}
          </div>

          <span v-if="showExclusiveText" class="exclusive-text">
            {{ exclusiveText }}
          </span>
        </div>

        <div class="stc-compact-product__price" data-testid="stc-compact-product-price">
          <ProductPrice :id="productId" :product="product" is-pricing-simplification />
        </div>

        <span v-if="selectedPrice?.isGWPAvailable" class="stc-compact-product__gwp-badge" data-testid="gwp-badge">
          {{ isMcom ? 'Free gift with purchase' : 'Gift with purchase' }}
        </span>

        <CompactColorSwatches
          v-if="colors?.length > 1"
          class="stc-compact-product__swatches"
          :colors="colors"
          :max-swatches="5"
          :selected-color-id="selectedColorId"
          :disabled="multiATBStore.isLoading"
          data-testid="stc-compact-product-swatches"
          @update:selected-color-id="handleColorUpdate"
        />
        <LowAvailability :product-id="productId" class="stc-compact-product__low-stock" data-testid="stc-compact-product-low-stock" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, provide } from 'vue';

import Picture from '@atomic-ui/picture/Picture';
import CompactColorSwatches from './CompactColorSwatches.vue';
import LowAvailability from '~/modules/availability/components/LowAvailability';

import { useBrand } from '~/composables/useBrand';
import useProductStore from '~/stores/useProductStore';
import { useProductProvide } from '~/modules/product/composables/useProductProvide';
import { useUpcValidatorProvide } from '~/modules/upc/composables/useUpcValidator';
import { useMarketPlace } from '~/composables/useMarketPlace';
import { useProductPriceProvide } from '~/modules/price/composables/useProductPriceProvide';
import { useCreateTypeStore } from '@nuxt-common/media-card-large/app/modules/type';
import TypeParser from '@nuxt-common/media-card-large/app/modules/type/parsers/TypeParser';
import { useImageryStateProvide } from '~/composables/useImageryState';

import { useProductFlags } from '~/modules/featureControl/composables/useProductFlags';
import { useProductPrice } from '~/modules/price/composables/useProductPrice';
import { useCommonProductGetters } from '~/composables/useCommonProductGetters';
import { useImagesProvide } from '~/composables/images/useImages';
import useColor from '~/modules/color/composables/useColor';
import { useShopTheCollectionUIStore } from '~/stores/useShopTheCollectionUIStore';
import { useMultiATBStore } from '~/stores/useMultiATBStore';
import ProductPrice from '~/modules/price/components';
import { AI_WATERMARK_CONFIG } from '~/constants/common';

const props = defineProps<{
  productId: ProductId;
}>();

const { isMcom } = useBrand();
const stcUIStore = useShopTheCollectionUIStore();
const multiATBStore = useMultiATBStore();

// Provide STC context for underlying components
provide('isSTC', true);

const memberProducts = useProductStore().getAllMembers;
const product = memberProducts[String(props.productId)];

// Initialize local context for member product
useProductProvide(props.productId, product);
useUpcValidatorProvide();
useMarketPlace();
useProductPriceProvide({ id: props.productId, product });
useImageryStateProvide();

useCreateTypeStore({
  id: props.productId,
  data: TypeParser(product, { defaultToFirst: isMcom }),
});

const selectedPrice = useProductPrice();
const { productName } = useCommonProductGetters();
const { primaryImageData } = useImagesProvide();
const { colors, selectedColorId } = useColor();
const { isExclusiveBrandFlag, isAiGeneratedMetaData } = useProductFlags();

const primaryImage = computed(() => primaryImageData.value?.path);
const productImgAlt = computed(() => `${productName.value} image`);

const imageSizes = {
  small: 96,
  medium: 130,
  large: 130,
  xlarge: 130,
};

const showExclusiveText = isMcom && isExclusiveBrandFlag;
const exclusiveText = "Macy's Exclusive";

const handleProductClick = () => {
  if (multiATBStore.isLoading) return;
  stcUIStore.openBottomSheet(String(props.productId));
};

const handleColorUpdate = (id: string | number) => {
  selectedColorId.value = id;
};

const aiImage = isAiGeneratedMetaData ? AI_WATERMARK_CONFIG.collectionCompact : undefined;
</script>






<style lang="scss" scoped>
@import '~settingSCSS';

.stc-compact-product {
  display: flex;
  flex-direction: column;
  cursor: pointer;
  position: relative;
  overflow: clip;
  padding: 16px;
  background: transparent;
  border: none;
  text-align: left;
  font-family: inherit;
  width: 100%;

  @include breakpoint(medium up) {
    padding: 16px 20px;
  }

  @include breakpoint(large up) {
    display: none;
  }
}

.stc-compact-product__overlay-btn {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  z-index: 1;
  outline: none;

  &:focus-visible {
    box-shadow: inset 0 0 0 2px $black;
  }
}

.stc-compact-product__main {
  display: flex;
  align-items: center;
  gap: 8px;

  @include breakpoint(medium up) {
    gap: 16px;
  }
}

.stc-compact-product__image-container {
  flex: 0 0 96px;
  width: 96px;
  min-width: 96px;
  max-width: 130px;
  height: auto;
  position: relative;
  background-color: $white;

  :deep(img) {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.stc-compact-product__details {
  flex: 1 0 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
}

.title-wrap {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.stc-compact-product__title {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  color: $black;
  min-width: 100%;
  width: min-content;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.stc-compact-product__price {
  width: 100%;
  display: flex;
  align-items: center;

  :deep(.price-layout) {
    @include margin-bottom-z;
  }

  :deep(.details-btn),
  :deep(.credit-finance-container),
  :deep(.additional-fees),
  :deep(.price-type-container),
  :deep(.promo-badge),
  :deep(.secondary-promo) {
    display: none !important;
  }
}

.stc-compact-product__gwp-badge {
  max-width: fit-content;
  white-space: nowrap;
  opacity: 0.95;
  display: flex;
  align-items: center;
  justify-content: center;
  max-height: 22px;
}

.stc-compact-product__low-stock {
  margin-bottom: 0 !important;
}


@import '~settingSCSS';

.stc-compact-product__title {
  @include body(large);
  line-height: 20px;
}

.stc-compact-product__gwp-badge {
  padding: 2px 4px;
  background-color: $background-color;
  color: $sale-color;
  @include body(heavy);
}
</style>